import { WA_PHYSICS } from '../core/constants';
import { getGameRNG } from '../core/rng';

/**
 * GunBound-style weather: every N turns (mode.weather.intervalTurns) a map
 * event is announced, then it activates on the next cycle and lasts until
 * the one after replaces it. The authority (server / offline game) rolls
 * events; online clients mirror the state via TurnSync.weather.
 */
export type WeatherType = 'windChange' | 'force' | 'tornado' | 'thor';

export interface WeatherEvent {
  type: WeatherType;
  /** Zone center x (force/tornado; cosmetic for the others). */
  x: number;
}

export interface WeatherSync {
  /** Announced for the next round. */
  next?: WeatherEvent;
  /** Active this round. */
  active?: WeatherEvent;
}

export const WEATHER_ZONE_WIDTH: Record<WeatherType, number> = {
  windChange: 0,
  force: 120,
  tornado: 90,
  thor: 0,
};

/** Top of the weather zone columns (negative y = up into the sky). The zones
 *  extend well above the world top so they cover the full height that shots
 *  arc through, not just the area below y=0. */
export const WEATHER_ZONE_TOP = -540;

export const WEATHER_LABELS: Record<WeatherType, string> = {
  windChange: 'Wind Change',
  force: 'Force Zone',
  tornado: 'Tornado',
  thor: 'Thor',
};

export const WEATHER_ACTIVE_MESSAGES: Record<WeatherType, string> = {
  windChange: 'WEATHER: the wind shifts!',
  force: 'WEATHER: Force zone is live (+25% damage through it)!',
  tornado: 'WEATHER: TORNADO!',
  thor: 'WEATHER: Thor is overhead!',
};

export const WEATHER_COLORS: Record<WeatherType, number> = {
  windChange: 0x99ddff,
  force: 0xffaa33,
  tornado: 0x99ccff,
  thor: 0xff5555,
};

/** GunBound vortex: forward, reflect, back, reflect, forward and out. */
const TORNADO_REFLECTIONS = 2;
/**
 * Inside the vortex, vertical motion is gently damped (the funnel carries
 * the shot) — low enough that the shot arc still bends recognizably.
 */
const TORNADO_SUSPEND_DAMPING = 0.6;
const FORCE_DAMAGE_MULTIPLIER = 1.25;

/** Thor satellite hover altitude (zap origin + rendering baseline). */
export const THOR_ALTITUDE = 70;
/**
 * The Thor satellite is a permanent fixture in the sky (hovers here): both
 * Thor weather and Aduka shots fire their bolts from this point.
 */
export const THOR_HOME_X = WA_PHYSICS.WORLD.WIDTH / 2;
export const THOR_ZAP_RADIUS = 30;
export const THOR_ZAP_DAMAGE = 25;

/**
 * Where Thor's zap lands: the first solid terrain along the satellite ->
 * target ray (zaps are blocked by land and blast holes into it), otherwise
 * the impact point itself.
 */
export function traceThorZap(
  fromX: number,
  fromY: number,
  targetX: number,
  targetY: number,
  isSolid: (x: number, y: number) => boolean,
): { x: number; y: number } {
  const dx = targetX - fromX;
  const dy = targetY - fromY;
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / 4));
  for (let i = 0; i <= steps; i++) {
    const x = fromX + (dx * i) / steps;
    const y = fromY + (dy * i) / steps;
    if (isSolid(x, y)) {
      return { x, y };
    }
  }
  return { x: targetX, y: targetY };
}

interface WeatherProjectile {
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  maxDamage: number;
}

export class WeatherSystem {
  private types: WeatherType[];
  private intervalTurns: number;
  private next: WeatherEvent | null = null;
  private active: WeatherEvent | null = null;
  private turnCount = 0;
  /** Projectiles already zapped by force/thor (damage boosts apply once). */
  private boosted = new WeakSet<object>();
  /** Mid-vortex reflection state per projectile (tornado pass). */
  private tornadoPasses = new WeakMap<object, { reflections: number }>();
  /** Projectiles that already completed their tornado pass. */
  private tornadoDone = new WeakSet<object>();
  private onWindChange?: () => void;

  constructor(types: string[], intervalTurns = 2, onWindChange?: () => void) {
    this.types = types.filter((t): t is WeatherType => t in WEATHER_ZONE_WIDTH);
    this.intervalTurns = Math.max(1, Math.floor(intervalTurns) || 2);
    this.onWindChange = onWindChange;
  }

  get enabled(): boolean {
    return this.types.length > 0;
  }

  getNext(): WeatherEvent | null {
    return this.next;
  }

  getActive(): WeatherEvent | null {
    return this.active;
  }

  /** Authority only, once per turn: every intervalTurns the announced event activates. */
  onTurnStarted(): void {
    if (!this.enabled) return;
    this.turnCount++;
    if ((this.turnCount - 1) % this.intervalTurns !== 0) return;
    this.active = this.next;
    this.next = this.roll();
    if (this.active?.type === 'windChange') {
      this.onWindChange?.();
    }
  }

  private roll(): WeatherEvent {
    const type = this.types[getGameRNG().nextInt(0, this.types.length - 1)];
    const x = getGameRNG().nextInt(250, WA_PHYSICS.WORLD.WIDTH - 250);
    return { type, x };
  }

  /** Zone physics + damage modifiers for projectiles in flight. */
  applyToProjectile(projectile: WeatherProjectile, deltaTime: number): void {
    const active = this.active;
    if (!active) return;

    if (active.type === 'tornado') {
      this.applyTornado(projectile, deltaTime, active.x);
    } else if (active.type === 'force') {
      if (Math.abs(projectile.position.x - active.x) <= WEATHER_ZONE_WIDTH.force / 2) {
        // Force zone: damage boost only (no speed change).
        this.boostDamage(projectile, FORCE_DAMAGE_MULTIPLIER);
      }
    }
    // Thor has no in-flight effect: it zaps every projectile impact point
    // instead (see traceThorZap; wired in the host runtimes).
  }

  /**
   * GunBound vortex (per StrategyWiki): the shot keeps its direction and
   * velocity inside, reflects off the far edge, travels back, reflects off
   * the near edge, then exits and resumes its original heading. The extra
   * hang time (with wind and the suspended fall) shifts where it lands.
   */
  private applyTornado(projectile: WeatherProjectile, deltaTime: number, centerX: number): void {
    if (this.tornadoDone.has(projectile)) return;
    const half = WEATHER_ZONE_WIDTH.tornado / 2;
    const dx = projectile.position.x - centerX;
    const pass = this.tornadoPasses.get(projectile);

    if (Math.abs(dx) <= half) {
      if (!pass) {
        this.tornadoPasses.set(projectile, { reflections: 0 });
      }
      // The funnel carries the shot: vertical motion is suspended inside.
      projectile.velocity.y *= Math.max(0, 1 - TORNADO_SUSPEND_DAMPING * deltaTime);
      return;
    }

    if (!pass) return; // passing nearby, never entered

    if (pass.reflections < TORNADO_REFLECTIONS) {
      // Reflect off the vortex edge back inside.
      const edge = centerX + (dx > 0 ? half : -half);
      projectile.position.x = 2 * edge - projectile.position.x;
      projectile.velocity.x = -projectile.velocity.x;
      pass.reflections++;
    } else {
      // Third crossing: the shot exits with its original heading restored.
      this.tornadoPasses.delete(projectile);
      this.tornadoDone.add(projectile);
    }
  }

  private boostDamage(projectile: WeatherProjectile, multiplier: number): void {
    if (this.boosted.has(projectile)) return;
    this.boosted.add(projectile);
    projectile.maxDamage = Math.round(projectile.maxDamage * multiplier);
  }

  getSync(): WeatherSync | undefined {
    if (!this.enabled) return undefined;
    return {
      next: this.next ?? undefined,
      active: this.active ?? undefined,
    };
  }

  /** Mirror the authoritative weather on online clients. */
  applySync(sync: WeatherSync | undefined): void {
    this.next = sync?.next ?? null;
    this.active = sync?.active ?? null;
  }
}
